import * as React from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

interface PageTransitionProps {
  children: React.ReactNode;
  className?: string;
  transition?: "fade" | "slide-up" | "slide-down" | "slide-left" | "slide-right" | "scale";
}

export const PageTransition = ({ 
  children, 
  className,
  transition = "fade" 
}: PageTransitionProps) => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = React.useState(location);
  const [transitionStage, setTransitionStage] = React.useState<"fade-in" | "fade-out">("fade-in");

  React.useEffect(() => {
    if (location.pathname !== displayLocation.pathname) {
      setTransitionStage("fade-out");
    }
  }, [location, displayLocation]);

  const onAnimationEnd = () => {
    if (transitionStage === "fade-out") {
      setDisplayLocation(location);
      setTransitionStage("fade-in");
    }
  };

  const transitionClasses = {
    fade: {
      "fade-in": "animate-fade-in",
      "fade-out": "animate-fade-out",
    },
    "slide-up": {
      "fade-in": "animate-slide-up",
      "fade-out": "animate-slide-down-out",
    },
    "slide-down": {
      "fade-in": "animate-slide-down",
      "fade-out": "animate-slide-up-out",
    },
    "slide-left": {
      "fade-in": "animate-slide-in-left",
      "fade-out": "animate-slide-out-left",
    },
    "slide-right": {
      "fade-in": "animate-slide-in-right",
      "fade-out": "animate-slide-out-right",
    },
    scale: {
      "fade-in": "animate-scale-in",
      "fade-out": "animate-scale-out",
    },
  };

  return (
    <div
      className={cn(
        "min-h-screen",
        transitionClasses[transition][transitionStage],
        className
      )}
      onAnimationEnd={onAnimationEnd}
    >
      {children}
    </div>
  );
};
